import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { AccountsSheetFilterCriteria, AccountsSheetFilterViewInput } from '@/lib/types';

/**
 * NARROWING THE EMBEDDED ACCOUNTS SHEET, FOR THIS READER.
 *
 * One `POST /accounts-dashboard/sheet/filter-view` per press, and the id it
 * answers with is what the frame's `#gid=…&fvid=…` link hangs off.
 *
 * ⚠️ **IT IS "DOES NOT DISTURB OTHERS", NOT "PRIVATE".** A Google *saved* filter
 * view is stored in the document and appears in every reader's filter-view list;
 * the genuinely per-session one (*Filter for me*) has no URL and cannot be made
 * through an API at all. **No copy built on this hook may call it private** — the
 * rule is written at `AccountsSheetFilterViewInput` and repeated here because
 * this is the file somebody reads before wording a control.
 *
 * ⚠️ **THE FAILURE PATH IS THE ONE THAT RUNS TODAY.** The mirror's service
 * account has never been granted access to the spreadsheet, so this call fails
 * for everybody until somebody shares it. Every branch below therefore has to
 * leave the caller able to say *the sheet is showing everything* — which is why
 * `error` is reported ALONGSIDE the previous `filterViewId` and never instead of
 * it, and why nothing here ever optimistically reports a filter as applied.
 */
export type AccountsSheetFilterSection = AccountsSheetFilterViewInput['section'];

export interface AccountsSheetFilterViewState {
  /**
   * The applied view's id, or `null` for "no filter view is on the frame".
   *
   * ⚠️ **A `null` FROM THE SERVER IS A SUCCESS**, not a failure: it is the answer
   * to an empty filter set — the reader's view was removed and `fvid` comes off
   * the URL. It is indistinguishable here from "nothing has been applied yet",
   * deliberately, because both mean exactly the same thing to the frame.
   */
  filterViewId: number | null;
  /**
   * Which section {@link filterViewId} belongs to.
   *
   * ⚠️ **A FILTER VIEW IS PER (READER, SECTION)**, and the sections share one
   * page. Without this, switching from Quotes to Invoices would hang the Quotes
   * view's id on the Invoices tab's link — a real id for the wrong tab, which
   * Google resolves to *something* rather than erroring. `null` while nothing
   * has been applied.
   */
  section: AccountsSheetFilterSection | null;
  applying: boolean;
  /**
   * What went wrong, for a reader who has to be told the frame beside it is
   * UNFILTERED. `null` is the ordinary state.
   */
  error: string | null;
  /**
   * The route does not exist on this server.
   *
   * ⚠️ **A `404` IS A CALM STATE, NOT AN ERROR** — this app deploys separately
   * from the API and can be newer than it, exactly as `useAccountsSheetLink`
   * already treats a `404` on the link read. It still means the sheet is
   * unfiltered, so a caller says so; it does not mean anything is broken.
   */
  notWired: boolean;
}

export interface AccountsSheetFilterViewApi extends AccountsSheetFilterViewState {
  apply: (section: AccountsSheetFilterSection, filters: AccountsSheetFilterCriteria) => void;
}

const IDLE: AccountsSheetFilterViewState = {
  filterViewId: null,
  section: null,
  applying: false,
  error: null,
  notWired: false,
};

export function useAccountsSheetFilterView(): AccountsSheetFilterViewApi {
  const [state, setState] = useState<AccountsSheetFilterViewState>(IDLE);

  /*
   * The in-flight request, so a second press abandons the first rather than
   * racing it — two presses landing out of order would leave the frame on the id
   * from the older filter set, with nothing on screen saying so.
   */
  const inFlight = useRef<AbortController | null>(null);
  useEffect(() => () => inFlight.current?.abort(), []);

  const apply = useCallback(
    (section: AccountsSheetFilterSection, filters: AccountsSheetFilterCriteria) => {
      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;

      /*
       * ⚠️ The previous `filterViewId` is KEPT here and in every failure branch
       * below. The frame is still showing that view; blanking the id would drop
       * `fvid` from the link, silently widening what is on screen at the moment
       * we are telling somebody a narrowing failed.
       */
      setState((previous) => ({ ...previous, applying: true, error: null }));

      api
        .createAccountsSheetFilterView({ section, filters }, controller.signal)
        .then((response) => {
          if (controller.signal.aborted) return;
          setState({
            filterViewId: response.data.filterViewId,
            section,
            applying: false,
            error: null,
            notWired: false,
          });
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;

          /*
           * ⚠️ **401 IS `api.ts`'S, ENTIRELY.** It runs the refresh-and-retry and,
           * where that fails too, clears the session and sends the reader to
           * `/login`. A message from here would be a second, worse account of
           * being signed out — printed next to a frame that is about to be
           * replaced by the sign-in page. Only `applying` is released, so nothing
           * is left stuck if the session survives.
           */
          if (caught instanceof ApiError && caught.status === 401) {
            setState((previous) => ({ ...previous, applying: false }));
            return;
          }

          if (caught instanceof ApiError && caught.status === 404) {
            setState((previous) => ({
              ...previous,
              applying: false,
              error: null,
              notWired: true,
            }));
            return;
          }

          setState((previous) => ({
            ...previous,
            applying: false,
            notWired: false,
            error:
              caught instanceof Error && caught.message
                ? caught.message
                : 'The sheet could not be filtered.',
          }));
        });
    },
    [],
  );

  return { ...state, apply };
}
